import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Link, Breadcrumbs } from '@mui/material';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import LandingNavbar from './LandingNavbar';
import Footer from './Footer';

interface LegalPageLayoutProps {
  title: string;
  updatedAt: string;
  seoTitle: string;
  seoDescription: string;
  canonicalPath: string;
  children: React.ReactNode;
}

const SITE_URL = 'https://www.medprimebeneficios.com.br';

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  updatedAt,
  seoTitle,
  seoDescription,
  canonicalPath,
  children,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = seoTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
      if (!el) return null;
      const previous = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return previous;
    };

    const previousDescription = setMeta('meta[name="description"]', 'content', seoDescription);
    const previousOgTitle = setMeta('meta[property="og:title"]', 'content', seoTitle);
    const previousOgDescription = setMeta('meta[property="og:description"]', 'content', seoDescription);
    const previousOgUrl = setMeta('meta[property="og:url"]', 'content', `${SITE_URL}${canonicalPath}`);
    const previousTwitterTitle = setMeta('meta[name="twitter:title"]', 'content', seoTitle);
    const previousTwitterDescription = setMeta('meta[name="twitter:description"]', 'content', seoDescription);

    let canonicalEl = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const previousCanonical = canonicalEl?.getAttribute('href') ?? null;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', `${SITE_URL}${canonicalPath}`);
    }

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null) setMeta('meta[name="description"]', 'content', previousDescription);
      if (previousOgTitle !== null) setMeta('meta[property="og:title"]', 'content', previousOgTitle);
      if (previousOgDescription !== null) setMeta('meta[property="og:description"]', 'content', previousOgDescription);
      if (previousOgUrl !== null) setMeta('meta[property="og:url"]', 'content', previousOgUrl);
      if (previousTwitterTitle !== null) setMeta('meta[name="twitter:title"]', 'content', previousTwitterTitle);
      if (previousTwitterDescription !== null) setMeta('meta[name="twitter:description"]', 'content', previousTwitterDescription);
      if (canonicalEl && previousCanonical !== null) canonicalEl.setAttribute('href', previousCanonical);
    };
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <Box>
      <LandingNavbar />

      <Container maxWidth="md" component="main" sx={{ py: { xs: 6, md: 10 } }}>
        <Breadcrumbs
          separator={<NavigateNextRoundedIcon fontSize="small" />}
          sx={{ mb: 3, fontSize: '0.85rem' }}
        >
          <Link component={RouterLink} to="/" underline="hover" color="text.secondary">
            Início
          </Link>
          <Typography color="text.primary" fontSize="0.85rem">
            {title}
          </Typography>
        </Breadcrumbs>

        <Typography variant="h1" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
          Última atualização: {updatedAt}
        </Typography>

        <Box
          sx={{
            '& h2': {
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.15rem', md: '1.3rem' },
              mt: 5,
              mb: 1.5,
            },
            '& p, & li': {
              fontSize: '0.95rem',
              lineHeight: 1.75,
              color: 'text.secondary',
            },
            '& ul': { pl: 3, mb: 2 },
            '& li': { mb: 0.75 },
            '& p': { mb: 2 },
            '& strong': { color: 'text.primary' },
          }}
        >
          {children}
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default LegalPageLayout;
